<template lang="pug">
OrderActionModal(v-if="show" :show="show" max-width="min(100vw - 2rem, 34rem)" @close="onClose")
  template(slot="header")
    span.order-novelty-modal__title.uppercase Novedades · {{ orderLabel }}

  .order-novelty-modal
    .order-novelty-modal__actions(v-if="order && recentDataEntries.length")
      shActionButton(
        :action="generateRemit"
        text="Generar remite"
        textRunning="Generando remite"
        :normalColor="true"
      )
      shActionButton(
        v-if="hasRemit"
        :action="printRemit"
        text="Imprimir"
        textRunning="Imprimiendo"
        :normalColor="true"
      )

    p.order-novelty-modal__empty(v-if="!recentDataEntries.length") No hay novedades de datos recientes.

    .order-novelty-modal__item(v-for="(entry, index) in recentDataEntries" :key="entryKey(entry, index)")
      .order-novelty-modal__item-header
        span.order-novelty-modal__item-title {{ changeLabel(entry.change) }}
        span.order-novelty-modal__item-date(v-if="entry.createdAt") {{ dateName(entry.createdAt) }} - {{ time(entry.createdAt) }}

      .order-novelty-modal__change(v-if="entry.from || entry.to")
        .order-novelty-modal__change-row
          span.order-novelty-modal__change-label Antes
          span.order-novelty-modal__change-value.order-novelty-modal__change-value--from {{ displayValue(entry.from) }}
        .order-novelty-modal__change-row
          span.order-novelty-modal__change-label Después
          span.order-novelty-modal__change-value.order-novelty-modal__change-value--to {{ displayValue(entry.to) }}

      .order-novelty-modal__meta(v-if="entry.createdBy")
        span.order-novelty-modal__meta-label Creado por
        shNameUser.order-novelty-modal__meta-user(:dni="entry.createdBy")
</template>

<script>
import { mapActions } from 'vuex'
import mFilters from '@/mixins/filters'
import router from '@my/router'
import OrderActionModal from '@/components/pages/order/refund/OrderActionModal.vue'
import shNameUser from '@/components/elements/NameUser'
import shActionButton from '@/components/elements/ActionButton'
import { recordHasRemit } from '@/helpers/remit'
import { mapPackOrder } from '@/store/modules/pack'

const CHANGE_LABELS = {
  'changed name': 'cambió nombre',
  'changed phone': 'cambió teléfono',
  'changed address': 'cambió dirección',
  'changed city': 'cambió ciudad',
  'changed department': 'cambió departamento',
  'changed location': 'cambió ubicación',
  'changed email': 'cambió email'
}

export default {
  name: 'OrderNoveltyModal',

  mixins: [mFilters],

  components: {
    OrderActionModal,
    shNameUser,
    shActionButton
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    }
  },

  computed: {
    orderLabel () {
      return (this.order && this.order.order) || ''
    },

    recentDataEntries () {
      const entries = ((this.order && this.order.noveltyData) || []).slice().reverse()
      const since = Date.now() - 30 * 24 * 60 * 60 * 1000

      return entries.filter(entry => {
        if (!entry.createdAt) return true
        return new Date(entry.createdAt).getTime() >= since
      })
    },

    hasRemit () {
      return recordHasRemit(this.order)
    }
  },

  methods: {
    ...mapActions('orderNovelty', ['generateNoveltyRemit', 'fetchDataNovelties', 'fetchDataNoveltyCount']),

    onClose () {
      this.$emit('close')
    },

    entryKey (entry, index) {
      const createdAt = entry.createdAt || ''
      return `${createdAt}-${index}-${entry.change || ''}`
    },

    changeLabel (change) {
      return CHANGE_LABELS[change] || change || '—'
    },

    displayValue (value) {
      if (!value) return '—'
      return value
    },

    generateRemit () {
      if (!this.order || !this.order.order) return Promise.resolve()
      return this.generateNoveltyRemit({ orderIds: [this.order.order] })
    },

    printRemit () {
      if (!this.hasRemit) return Promise.resolve()

      this.$store.commit('setPrintReturnAddress', {
        orders: [mapPackOrder(this.order)],
        remitOnly: true
      })
      router.goToPrintReturnAddress()
    }
  }
}
</script>

<style lang="stylus" scoped>
.order-novelty-modal
  padding 0 1.5rem 1.5rem
  overflow-y auto
  max-height calc(100vh - 8rem)

.order-novelty-modal__title
  font-size 1rem
  font-weight 600

.order-novelty-modal__actions
  display flex
  flex-wrap wrap
  gap .75rem
  margin-bottom 1.25rem

.order-novelty-modal__empty
  margin 0
  font-size .95rem
  opacity .7

.order-novelty-modal__item
  padding 1rem 0
  border-bottom 1px solid var(--color-border)

  &:last-child
    border-bottom none

.order-novelty-modal__item-header
  display flex
  flex-wrap wrap
  align-items baseline
  justify-content space-between
  gap .5rem
  margin-bottom .75rem

.order-novelty-modal__item-title
  font-size 1rem
  font-weight 600
  text-transform capitalize

.order-novelty-modal__item-date
  font-size .85rem
  color var(--color-placeholder)

.order-novelty-modal__meta
  display flex
  flex-wrap wrap
  align-items baseline
  gap .35rem .5rem
  margin-bottom .35rem
  font-size .9rem

.order-novelty-modal__meta-label
  color var(--color-placeholder)
  text-transform uppercase
  font-size .8rem

.order-novelty-modal__meta-user
  font-size .85rem
  opacity .85

.order-novelty-modal__change
  display flex
  flex-direction column
  gap .35rem
  margin .75rem 0
  padding .85rem 1rem
  background var(--color-light)
  border 1px solid var(--color-border)
  border-radius .35rem

.order-novelty-modal__change-row
  display grid
  grid-template-columns 5rem minmax(0, 1fr)
  gap .35rem .75rem
  font-size .95rem
  line-height 1.35

.order-novelty-modal__change-label
  color var(--color-placeholder)
  text-transform uppercase
  font-size .8rem

.order-novelty-modal__change-value
  word-break break-word

.order-novelty-modal__change-value--from
  color var(--color-red)
  text-decoration line-through

.order-novelty-modal__change-value--to
  color var(--color-font-principal)
  font-weight 600
</style>



// WEBPACK FOOTER //
// src/components/elements/order/OrderNoveltyModal.vue